"use client";
import FlexCardGrid from "@/components/CardGrid/variants/Flex";
import {
  HOMEPAGE_HERO_DARK_BG,
  HOMEPAGE_HERO_LIGHT_BG,
} from "@/components/Homepage/HomepageHero/constants";
import type { BaseHomepageHeroProps } from "@/components/Homepage/HomepageHero/types";
import HomepageHeroBackgroundImage from "@/components/Homepage/HomepageHero/components/HomepageHeroBackgroundImage";
import HomepageHeroHeading from "@/components/Homepage/HomepageHero/components/HomepageHeroHeading";
import { getTranslatedPopularTopicCards } from "@/components/CardGrid/utils/getTranslatedPopularTopicCards";
import useTranslations from "@/i18n/useTranslations";

export default function BaseHomepageHeroCategory({
  title,
  titleColor,
  descriptionItems,
  descriptionColor,
  desktopImage,
  tabletImage,
  mobileImage,
  theme = "dark",
  headingLevel,
  eventing,
}: BaseHomepageHeroProps) {
  const t = useTranslations("Homepage");
  const cards = getTranslatedPopularTopicCards(t);
  const bgFallback = !desktopImage
    ? theme === "light"
      ? HOMEPAGE_HERO_LIGHT_BG
      : HOMEPAGE_HERO_DARK_BG
    : "";
  return (
    <div className={`relative h-full ${bgFallback}`}>
      {/* Background Image */}
      {desktopImage && (
        <>
          <HomepageHeroBackgroundImage
            imageSrc={mobileImage ?? tabletImage ?? desktopImage}
            screenSize="mobile"
            theme={theme}
          />
          <HomepageHeroBackgroundImage
            imageSrc={tabletImage ?? desktopImage}
            screenSize="tablet"
            theme={theme}
          />
          <HomepageHeroBackgroundImage
            imageSrc={desktopImage}
            screenSize="desktop"
            theme={theme}
          />
        </>
      )}

      <div className="container flex flex-col items-center xl:max-w-screen-2xl p-8 text-center category-hero">
        {/* Title, Description */}
        <HomepageHeroHeading
          title={title}
          titleColor={titleColor}
          descriptionItems={descriptionItems}
          descriptionColor={descriptionColor}
          headingLevel={headingLevel}
          headingClassName="elm-d1 font-black italic p-0 mt-0 mb-4"
        />

        {/* Categories */}
        <FlexCardGrid
          cards={cards}
          className="pt-0 pb-6"
          gridClassName="!flex flex-wrap justify-center items-stretch gap-8"
          gridItemClassName={`px-2 sm:h-[160px] w-auto md:h-auto md:w-auto md:max-w-none lg:w-auto lg:h-auto basis-full sm:basis-[45%] xl:basis-[17%] ${theme === "light" ? "hover:shadow-putty-700" : "hover:shadow-black"}`}
          gridItemContentClassName="gap-2 sm:flex-col sm:justify-center items-center [&_img]:mx-auto sm:[&_span]:text-center md:[&_span]:text-md [&_img]:mx-0 [&_img]:lg:h-12 [&_img]:lg:w-20 [&_img]:lg:object-cover"
          html_id={eventing?.sectionHtmlId ?? "topic-icon-card"}
          location={eventing?.sectionLocation ?? "hero"}
        />
      </div>
    </div>
  );
}
